/**
 *
 *  Google OAuth Provider
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 26/06/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, {
  useContext,
  createContext,
  useMemo,
  ReactNode,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";

// Utils
import { loadScript } from "../../utils";

const GOOGLE_GSI_CLIENT_URL = `https://accounts.google.com/gsi/client`;
const GOOGLE_GSI = "google-gsi";

const GoogleOAuthContext = createContext(null);

export default function GoogleOAuthProvider({
  clientId,
  nonce,
  onScriptLoadSuccess = () => {},
  onScriptLoadError,
  children,
  ...rest
}) {
  const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] =
    useState(false);
  const isMounted = useRef(true); // Create a ref to track if the component is mounted

  const handleLoaded = useCallback(() => {
    if (isMounted.current) {
      // Check if the component is still mounted
      setScriptLoadedSuccessfully(true);
    }

    onScriptLoadSuccess();
  }, [onScriptLoadSuccess]);

  useEffect(() => {
    loadScript(GOOGLE_GSI_CLIENT_URL, GOOGLE_GSI, handleLoaded, true);

    return () => {
      isMounted.current = false; // Set the flag to false when unmounted
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      clientId,
      scriptLoadedSuccessfully,
    }),
    [clientId, scriptLoadedSuccessfully]
  );

  return (
    <GoogleOAuthContext.Provider value={contextValue}>
      {React.cloneElement(children, { ...rest })}
    </GoogleOAuthContext.Provider>
  );
}

export function useGoogleOAuth() {
  const context = useContext(GoogleOAuthContext);

  if (!context) {
    throw new Error(
      "Google OAuth components must be used within GoogleOAuthProvider"
    );
  }
  return context;
}
